import { Injectable } from '@angular/core';
import { Observable, of, combineLatest } from 'rxjs';
import { catchError, map, shareReplay, switchMap } from 'rxjs/operators';
import { ApiService } from './api.service';

export type DashboardContext = 'ORGANIZATION' | 'USER';

export interface BasePayload {
  context: DashboardContext;
  businessId?: string;
  userId?: string;
}

export interface OfferPipelineParams {
  filter?: 'DAY' | 'WEEK' | 'MONTH' | 'YEAR' | 'DATE' | null;
  fromDate?: string | null;
  toDate?: string | null;
  limit?: number;
  searchAfterKey?: any[] | null;
}

export interface RecentSubmissionsParams {
  limit?: number;
  searchAfterKey?: any[] | null;
}

export interface TodaysActivityParams {
  limit?: number;
  searchAfterKey?: any[] | null;
}

export interface FilterParams {
  limit?: number;
  searchAfterKey?: any[] | null;
  searchContent?: string;
  status?: string[];
}

/** `weeklyApplicants` block on the pipeline-stats response — one count per
 *  weekday. Backend emits Title-Case keys (`Monday`…`Sunday`); an earlier
 *  draft used UPPERCASE, so the index signature keeps both valid. Values may
 *  arrive as strings, hence `number | string`. */
export interface WeeklyApplicants {
  Monday?: number | string;
  Tuesday?: number | string;
  Wednesday?: number | string;
  Thursday?: number | string;
  Friday?: number | string;
  Saturday?: number | string;
  Sunday?: number | string;
  [day: string]: number | string | undefined;
}

/** `data` block of the pipeline-stats response. Funnel counts are read
 *  directly off this object by `mapRecruiterFunnel`; `weeklyApplicants` feeds
 *  the "Applicants This Week" mini-chart via `mapWeeklyApplicants`. Every
 *  field is optional and may be a string — consumers coerce with
 *  `Number(... ?? 0)`. */
export interface RecruiterJobPipelineStatsData {
  applicants?: number | string;
  shortlisted?: number | string;
  interview?: number | string;
  selected?: number | string;
  offered?: number | string;
  onboarded?: number | string;
  weeklyApplicants?: WeeklyApplicants | null;
}

/** Response envelope for POST `jobs/getBusinessJobPipelineStats`. Success is
 *  signalled by any of `code === '00000'`, `code === 1`, or
 *  `status === 'SUCCESS'` (rolling-deploy tolerance). */
export interface RecruiterJobPipelineStatsResponse {
  code?: string | number;
  status?: string;
  data?: RecruiterJobPipelineStatsData;
}

/**
 * Centralizes the 5 POST /dashboard/* API calls and the ORGANIZATION-vs-USER
 * context decision. ORGANIZATION is sent only when the logged-in user is a
 * business super-admin (localStorage `businessRole === 'SUPERADMIN'`);
 * otherwise USER.
 */
@Injectable({ providedIn: 'root' })
export class DashboardService {
  constructor(private api: ApiService) {}

  /** Holds the ids fetched for the CURRENT dashboard load. Set by `primeIds()`
   *  (one fresh fetch per load) and reused by every payload builder during that
   *  load, so a dashboard that fires ~8 endpoints makes ONE id lookup, not 8. */
  private idsForLoad$: Observable<{ businessId: string; organisationId: string }> | null = null;

  /** ONE fresh fetch of the user's OWN business + organisation id from
   *  `business/findUserRoleInBusiness/{userId}` (both live under
   *  `data.businessData`). On success the values are also written back to
   *  localStorage so the fallback below stays current. On failure / no business
   *  the last-known localStorage values are used, so a transient error doesn't
   *  silently drop a super-admin out of ORGANIZATION scope. A user with no
   *  business resolves to empty ids — harmless, since only super-admins send
   *  them. */
  private fetchIds(): Observable<{ businessId: string; organisationId: string }> {
    const userId = localStorage.getItem('userId') || '';
    const fallback = {
      businessId: localStorage.getItem('myBusinessId') || '',
      organisationId: localStorage.getItem('organisationId') || '',
    };
    if (!userId) return of(fallback);
    return this.api.query('business/findUserRoleInBusiness/' + userId).pipe(
      map((res: any) => {
        const bd = res?.data?.businessData;
        if (res?.code === '00000' && bd) {
          const ids = {
            businessId: bd.businessId || fallback.businessId,
            organisationId: bd.organisationId || fallback.organisationId,
          };
          if (ids.businessId) localStorage.setItem('myBusinessId', ids.businessId);
          if (ids.organisationId) localStorage.setItem('organisationId', ids.organisationId);
          return ids;
        }
        return fallback;
      }),
      catchError(() => of(fallback)),
    );
  }

  /** Resolve the ids FRESH once per dashboard load. The component calls this in
   *  `ngOnInit` (before firing its API family); the result is shared via
   *  `shareReplay(1)` so every service call in that same load reuses it. Each
   *  remount re-primes, so a role / business switch is always reflected. */
  primeIds(): Observable<{ businessId: string; organisationId: string }> {
    this.idsForLoad$ = this.fetchIds().pipe(shareReplay(1));
    return this.idsForLoad$;
  }

  /** Used internally by every payload builder. Reuses the load-scoped ids when
   *  primed (the normal path); otherwise falls back to a one-off fresh fetch —
   *  defensive cover for a paginated "Load more" or any direct call that ran
   *  without `primeIds()` first. */
  private resolveIds(): Observable<{ businessId: string; organisationId: string }> {
    return this.idsForLoad$ ?? this.fetchIds();
  }

  /** Builds the `/dashboard/*` family base payload and runs `fn` with it.
   *  Non-super-admins use USER context (userId only) — no business id needed,
   *  so the fresh-id fetch is skipped entirely. Super-admins resolve their
   *  business id fresh, then send ORGANIZATION context.
   *
   *  `userId` is always included — even for ORGANIZATION — because the backend
   *  uses it to resolve the viewer's timezone (per ThisWeekSummaryAPI.md /
   *  BENCH_SALES_DASHBOARD_APIS.md). */
  private withBase(fn: (base: BasePayload) => Observable<any>): Observable<any> {
    const userId = localStorage.getItem('userId') || '';
    const isSuperAdmin = localStorage.getItem('businessRole') === 'SUPERADMIN';
    if (!isSuperAdmin) {
      return fn({ context: 'USER', userId });
    }
    return this.resolveIds().pipe(
      switchMap(ids => fn({ context: 'ORGANIZATION', businessId: ids.businessId, userId })),
    );
  }

  /** Strict `{context, businessId|userId}` — never both. Per
   *  Dashboard_API_Documentation.html §4.1. Used by the today's-activity
   *  and pipeline-stats endpoints (jobs/getBusinessJobPipelineStats,
   *  candidates/getBusinessCandidatePipelineStats,
   *  jobs/getBusinessTodaysActivity, candidates/getBusinessTodaysActivity).
   *  Same fresh-id rule as `withBase`. */
  private withStrictBase(fn: (base: BasePayload) => Observable<any>): Observable<any> {
    const isSuperAdmin = localStorage.getItem('businessRole') === 'SUPERADMIN';
    if (!isSuperAdmin) {
      return fn({ context: 'USER', userId: localStorage.getItem('userId') || '' });
    }
    return this.resolveIds().pipe(
      switchMap(ids => fn({ context: 'ORGANIZATION', businessId: ids.businessId })),
    );
  }

  // ── Recruiter Dashboard ────────────────────────────────────────────
  recruiterWeeklyMetrics(): Observable<any> {
    return this.withBase(base => this.api.create('dashboard/weekly-activity-metrics', base));
  }

  /** Slots widget — purpose-built endpoint that returns plan name, used,
   *  open, allocated, usagePercentage, and totalPosted in one shot.
   *  `type`: `'JOB'` for the recruiter dashboard, `'CANDIDATE'` for bench. */
  getJobCandidateSlots(type: 'JOB' | 'CANDIDATE'): Observable<any> {
    return this.withBase(base => this.api.create('dashboard/job-candidate-slots', {
      ...base,
      type,
    }));
  }

  /** This Week summary — six weekly metrics with current-week count + WoW
   *  change. `type` selects the dashboard variant: `'JOB'` for recruiter
   *  (activeJobs/applicants/interviews/shortlisted/offersSent/placements),
   *  `'CANDIDATE'` for bench (onBench/submissions/interviews/followUpsDue/
   *  offersOut/placements). */
  /** Backend v1.0.252 (Apr 2026) renamed the `type` enum from `'JOB'`
   *  to `'RECRUITER'`. `'CANDIDATE'` is unchanged for bench sales. */
  getThisWeekSummary(type: 'RECRUITER' | 'CANDIDATE'): Observable<any> {
    return this.withBase(base => this.api.create('dashboard/this-week-summary', {
      ...base,
      type,
    }));
  }

  recruiterOfferPipeline(p: OfferPipelineParams = {}): Observable<any> {
    return this.withBase(base => this.api.create('dashboard/offer-pipeline', {
      ...base,
      fromDate: p.fromDate ?? null,
      toDate: p.toDate ?? null,
      limit: p.limit ?? 5,
      searchAfterKey: p.searchAfterKey ?? null,
    }));
  }

  // ── Bench Sales Dashboard ─────────────────────────────────────────
  benchWeeklyMetrics(): Observable<any> {
    return this.withBase(base => this.api.create('dashboard/bench-sales/weekly-activity-metrics', base));
  }

  benchOfferPipeline(p: OfferPipelineParams = {}): Observable<any> {
    return this.withBase(base => this.api.create('dashboard/bench/offer-pipeline', {
      ...base,
      fromDate: p.fromDate ?? null,
      toDate: p.toDate ?? null,
      limit: p.limit ?? 5,
      searchAfterKey: p.searchAfterKey ?? null,
    }));
  }

  recentSubmissions(p: RecentSubmissionsParams = {}): Observable<any> {
    return this.withBase(base => this.api.create('dashboard/recentSubmissions', {
      ...base,
      limit: p.limit ?? 5,
      searchAfterKey: p.searchAfterKey ?? null,
    }));
  }

  /** Recruiter — Active Jobs KPI + Total Applicants KPI + Hiring Pipeline funnel.
   *
   *  Sends `{context, businessId|userId, status}` per
   *  Dashboard_API_Documentation.html §2.2 — only the id field matching
   *  `context` is sent (never both). Response also exposes `activeJobsStats`
   *  (week-over-week) and `todayApplicants` (day-over-day) — available for
   *  the KPI tile subtitles + trend chips if product wants them (not
   *  wired yet). */
  recruiterJobPipelineStats(status: string = 'ACTIVE'): Observable<RecruiterJobPipelineStatsResponse> {
    return this.withStrictBase(base => this.api.create('jobs/getBusinessJobPipelineStats', {
      ...base,
      status,
    }));
  }

  /** Recruiter — Today's Activity feed (events + view summary).
   *  Uses `withStrictBase()` per Dashboard_API_Documentation.html §2.1 so
   *  Individual / Employee logins (USER context) also get their own feed,
   *  not just super-admins. */
  recruiterTodaysActivity(p: TodaysActivityParams = {}): Observable<any> {
    return this.withStrictBase(base => this.api.create('jobs/getBusinessTodaysActivity', {
      ...base,
      limit: p.limit ?? 5,
      searchAfterKey: p.searchAfterKey ?? null,
    }));
  }

  /** Bench Sales — Bench Candidates/Submissions KPIs + Submission Pipeline
   *  funnel + rates. Payload is `{context, businessId|userId, status}` —
   *  `status` is required. */
  benchCandidatePipelineStats(status: string = 'ACTIVE'): Observable<any> {
    return this.withStrictBase(base => this.api.create('candidates/getBusinessCandidatePipelineStats', {
      ...base,
      status,
    }));
  }

  /** Bench Sales — Today's Activity feed (events + view summary).
   *  Same `withStrictBase()` switch as the recruiter side. */
  benchTodaysActivity(p: TodaysActivityParams = {}): Observable<any> {
    return this.withStrictBase(base => this.api.create('candidates/getBusinessTodaysActivity', {
      ...base,
      limit: p.limit ?? 5,
      searchAfterKey: p.searchAfterKey ?? null,
    }));
  }

  // ── Current user (for welcome banner greeting) ────────────────────
  getCurrentUser(): Observable<any> {
    const id = localStorage.getItem('userId') || '';
    return this.api.query('user/' + id);
  }

  // ── Active Jobs + Active Candidates (business-scoped filter APIs) ─

  /** Super-admin payload for `jobs/filter` / `candidates/filter` — the
   *  simple business-scoped shape originally used by these endpoints.
   *  `businessId` + `organisationId` come from the fresh `resolveIds()`
   *  lookup, not localStorage. */
  private filterPayload(p: FilterParams, ids: { businessId: string; organisationId: string }) {
    return {
      businessId: ids.businessId,
      userId: localStorage.getItem('userId') || '',
      organisationId: ids.organisationId,
      limit: p.limit ?? 5,
      searchAfterKey: p.searchAfterKey ?? null,
      searchContent: p.searchContent ?? '',
      status: p.status ?? [],
    };
  }

  /** Non-super-admin payload for `jobs/filter` — mirrors the exact body
   *  `/newjobs` sends when its source filter is set to "My Jobs", so the
   *  dashboard Active Jobs list matches the `/newjobs?source=myJobs`
   *  view for regular users (who don't have SUPERADMIN privileges). */
  private filterPayloadMyJobs(p: FilterParams, ids: { businessId: string }) {
    return {
      source: {
        all: false,
        marketPlaceJobs: false,
        myJobs: true,
        teamJobs: false,
        recruiterNetworkJobs: false,
        freeLancerNetworkJobs: false,
        mtaNetworkJobs: false,
        featured: false,
        suggestedJobs: false,
      },
      searchAfterKey: p.searchAfterKey ?? null,
      workMode: [],
      experienceFrom: null,
      experienceTo: null,
      postedFrom: null,
      postedTo: null,
      country: null,
      state: null,
      city: null,
      zipCode: null,
      limit: p.limit ?? 5,
      userId: localStorage.getItem('userId') || '',
      jobClassification: [],
      clientType: [],
      status: p.status ?? [],
      searchContent: p.searchContent ?? '',
      jobsToFilter: null,
      organisationTypeParams: {
        owner: false,
        systemsIntergrator: false,
        vendorStaffingAgency: false,
        primeVendor: false,
      },
      personalNetworks: [],
      businessNetworks: [],
      customers: [],
      businessId: ids.businessId,
    };
  }

  /** Non-super-admin payload for `candidates/filter` — mirrors
   *  `/newcandidates?source=myCandidates` so regular users see their
   *  own bench instead of an empty state on the dashboard. */
  private filterPayloadMyCandidates(p: FilterParams) {
    return {
      source: {
        all: false,
        myCandidates: true,
        marketPlaceCandidates: false,
        teamCandidates: false,
        benchNetworkCandidates: false,
        freeLancerNetworkCandidates: false,
        mtaNetworkCandidates: false,
        jobSeekerNetworkCandidates: false,
        featured: false,
        suggestedCandidates: false,
      },
      workMode: [],
      workAuthorization: [],
      engagementType: [],
      availableIn: [],
      status: p.status ?? [],
      searchAfterKey: p.searchAfterKey ?? null,
      city: null,
      state: null,
      country: null,
      zipCode: null,
      submissionFilters: [],
      postedFrom: null,
      postedTo: null,
      limit: p.limit ?? 5,
      userId: localStorage.getItem('userId') || '',
      searchContent: p.searchContent ?? '',
      candidatesToFilter: null,
    };
  }

  // ── Top Candidate Spotlight (driven by a selected job) ─────────────

  /** Cached comma-joined list of every itemId under
   *  `JOB_APPLICATION_BUYER_ACTIONS`. Mirrors the payload the
   *  `job-applicants` page uses for its "All" stage filter so the
   *  spotlight Applicants tab returns rows for every stage, not just
   *  the 3 defaults (APPLIED / SHORTLISTED / CANDIDATE-SHORTLISTED).
   *  `shareReplay(1)` → fetched once per app session. */
  private allApplicantStatuses$: Observable<string> | null = null;

  private getAllApplicantStatuses(): Observable<string> {
    if (!this.allApplicantStatuses$) {
      this.allApplicantStatuses$ = this.api
        .create('listvalue/findbyList', { domain: 'JOB_APPLICATION_BUYER_ACTIONS,JOB_APPLICATION_BLOCKING_ACTIONS' })
        .pipe(
          map((res: any) => {
            const items = res?.data?.JOB_APPLICATION_BUYER_ACTIONS?.listItems || [];
            return items.map((x: any) => x.itemId).join(',');
          }),
          shareReplay(1),
        );
    }
    return this.allApplicantStatuses$;
  }

  /** Applicants for a specific job — offset-paginated via `pageNumber`.
   *  Uses the full buyer-action status list so the spotlight count
   *  matches what the `/newjobs` Applicants page shows under "All". */
  findAppliedCandidates(jobId: string, limit: number = 5, pageNumber: number = 0): Observable<any> {
    return combineLatest([this.resolveIds(), this.getAllApplicantStatuses()]).pipe(
      switchMap(([ids, currentStatus]) => this.api.create('candidates/findAppliedCandidates', {
        jobId,
        candidatesApplied: [],
        currentStatus,
        userId: null,
        limit,
        pageNumber,
        searchText: '',
        subContracting: false,
        businessId: ids.businessId,
      })),
    );
  }

  /** Talent-pool matches for a specific job — cursor-paginated via `searchAfterKey`. */
  findTalentPoolMatches(jobId: string, limit: number = 5, searchAfterKey: any = null): Observable<any> {
    return this.api.create('jobs/findTalentPoolMatches', {
      jobId,
      limit,
      groupType: 'TALENT_POOL',
      searchAfterKey,
      populate: 'user',
    });
  }

  /** Marketplace (AI-suggested) candidates — cursor-paginated via `searchAfterKey`. */
  findTopSuggestedCandidates(jobId: string, limit: number = 5, searchAfterKey: any = null): Observable<any> {
    return this.api.create('candidates/findTopSuggestedCandidates', {
      jobId,
      limit,
      groupType: 'SUGGESTED_CANDIDATE',
      searchContent: '',
      searchAfterKey,
    });
  }

  // ── Top Job Spotlight (driven by a selected candidate) ─────────────

  /** Jobs this candidate has applied to — cursor-paginated via `searchAfterKey`. */
  findAppliedJobs(candidateId: string, searchAfterKey: any = null): Observable<any> {
    return this.api.create('jobs/findAppliedJobs', {
      candidateId,
      searchAfterKey,
      currentStatus: '',
      searchText: '',
    });
  }

  /** Marketplace (AI-suggested) jobs for a candidate — cursor-paginated. */
  findTopSuggestedJobs(candidateId: string, limit: number = 5, searchAfterKey: any = null): Observable<any> {
    return this.api.create('jobs/findTopSuggestedJobs', {
      candidateId,
      limit,
      groupType: 'SUGGESTED_JOB',
      searchContent: '',
      searchAfterKey,
    });
  }

  /** Recruiter — Active Jobs list (paginated). Resolves the business +
   *  organisation id fresh, then branches:
   *  - SUPERADMIN with a resolved businessId → `jobs/filter` with the
   *    business-scoped `filterPayload()` shape.
   *  - Everyone else → the same endpoint with the richer
   *    `/newjobs?source=myJobs` body so they see their own posted jobs
   *    instead of an empty list. */
  getActiveJobs(p: FilterParams = {}): Observable<any> {
    return this.resolveIds().pipe(
      switchMap(ids => {
        const isSuperAdmin = localStorage.getItem('businessRole') === 'SUPERADMIN';
        const payload = (isSuperAdmin && ids.businessId)
          ? this.filterPayload(p, ids)
          : this.filterPayloadMyJobs(p, ids);
        return this.api.create('jobs/filter', payload);
      }),
    );
  }

  /** Bench Sales — Active Candidates list (paginated). Same super-admin
   *  vs. regular-user split as `getActiveJobs` — regulars get the
   *  `/newcandidates?source=myCandidates` body. */
  getActiveCandidates(p: FilterParams = {}): Observable<any> {
    return this.resolveIds().pipe(
      switchMap(ids => {
        const isSuperAdmin = localStorage.getItem('businessRole') === 'SUPERADMIN';
        if (isSuperAdmin && ids.businessId) {
          const payload: any = this.filterPayload(p, ids);
          payload.submissionFilters = [];  // required by candidates/filter
          return this.api.create('candidates/filter', payload);
        }
        return this.api.create('candidates/filter', this.filterPayloadMyCandidates(p));
      }),
    );
  }
}
